import {noop} from '@benchling/js-utils';

import * as NavConstants from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileStates from 'FileStates';
import type {FileSummary} from 'folder-items/FileConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus, createChatWithDraftMessage} from 'llm/chat-library/ChatActions';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {getNoteLinkedFile} from 'notebook/notes/attachments/NoteLinkedFileDAO';
import {NoteLinkedFileTabState} from 'notebook/notes/attachments/NoteLinkedFileTab';
import type {OmniboxActionItem} from 'omnibox/OmniboxAction';
import type {OmniboxHelpDocsResult, OmniboxResponse} from 'omnibox/omniboxTypes';
import type {AssignedProcessTasksQuery} from 'processes/home/__generated/AssignedProcessTasksQuery';
import {setTaskSearchFilter} from 'processes/home/AssignedProcessTaskStatusSummary';

import {getObjectTypeDisplayName, RECENT_ITEMS_LIMIT} from './omniboxConstants';

let attachmentPreviewAbortController: AbortController | null = null;

export function abortAttachmentPreview(): void {
  attachmentPreviewAbortController?.abort();
}

async function openAskWithQuery(rewrittenQuery: string): Promise<void> {
  const chatType = getChatTypeForNewAgentChat(AgentType.ASK);
  const {chatSessionId} = await createChatWithDraftMessage(chatType, {
    draft: {
      status: ChatDraftStatus.DRAFT,
      message: {text: rewrittenQuery, selectedFiles: []},
      settings: {agentType: AgentType.ASK, publicDataEnabled: false},
    },
  });
  openExistingChat(chatSessionId);
  SidepanelActions.openSection(NavConstants.SECTIONS.AI);
}

function openNoteLinkedFileTab(
  entryApiId: string,
  fileId: string,
  fileName: string,
  previewFile: Parameters<typeof NoteLinkedFileTabState.open>[3] | null,
  externalFile: Parameters<typeof NoteLinkedFileTabState.open>[4],
  isPreviewTransformed: boolean
): void {
  const fileState = FileStates.get().get(entryApiId);
  if (fileState?.buffers()) {
    const existingTab = fileState.findTab(
      NoteLinkedFileTabState,
      (tab: NoteLinkedFileTabState) => tab.noteLinkedFileId() === fileId
    );
    if (existingTab != null) {
      existingTab.set({active: true});
      return;
    }
    fileState.buffers().addTab(
      new NoteLinkedFileTabState({
        owningEntityApiId: entryApiId,
        noteLinkedFileId: fileId,
        fileName,
        previewFile,
        externalFile,
        isPreviewTransformed,
      })
    );
  }
}

export async function openAttachmentPreview(
  entryApiId: string,
  noteLinkedFileId: string | null
): Promise<void> {
  if (!noteLinkedFileId) {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  let file;
  try {
    file = await getNoteLinkedFile({uuid: noteLinkedFileId, entryId: entryApiId});
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  if (!file.externalFile) {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  // If the entry is already open, add the tab directly
  const fileState = FileStates.get().get(entryApiId);
  if (fileState?.buffers()) {
    openNoteLinkedFileTab(
      entryApiId,
      file.id,
      file.name,
      file.previewFile,
      file.externalFile,
      file.isPreviewTransformed ?? false
    );
    return;
  }

  // Entry not open — navigate to it first, then add the tab once it loads
  FileBrowserActions.openItem(entryApiId);
  attachmentPreviewAbortController?.abort();
  attachmentPreviewAbortController = new AbortController();
  const signal = attachmentPreviewAbortController.signal;
  let attempts = 0;
  const interval = setInterval(() => {
    if (signal.aborted) {
      clearInterval(interval);
      return;
    }
    attempts++;
    const state = FileStates.get().get(entryApiId);
    if (state?.buffers()) {
      clearInterval(interval);
      openNoteLinkedFileTab(
        entryApiId,
        file.id,
        file.name,
        file.previewFile,
        file.externalFile!,
        file.isPreviewTransformed ?? false
      );
    } else if (attempts >= 50) {
      clearInterval(interval);
    }
  }, 100);
}

export function buildDefaultActions(
  taskSummaries: AssignedProcessTasksQuery | undefined,
  recentItems: Array<FileSummary>
): Array<OmniboxActionItem> {
  const actions: Array<OmniboxActionItem> = [];

  if (taskSummaries) {
    const userId = taskSummaries.currentUser?.id ?? null;
    for (const summary of taskSummaries.assignedProcessTaskStatusSummaries) {
      actions.push({
        id: `task-status-${summary.status.id}`,
        title: `${summary.count} ${summary.status.displayName}`,
        group: 'Tasks Assigned to Me',
        resultType: 'assigned_task_status',
        onTrigger: () => {
          setTaskSearchFilter(userId, summary.status.id);
        },
      });
    }
  }

  for (const item of recentItems.slice(0, RECENT_ITEMS_LIMIT)) {
    const modifiedAt = (item as FileSummary & {modified_at?: string}).modified_at;
    actions.push({
      id: `recent-${item.id}`,
      title: item.name,
      group: 'Recently Viewed',
      resultType: 'recent_item',
      objectType: getObjectTypeDisplayName(item.id),
      apiId: item.id,
      modifiedAt: modifiedAt ?? undefined,
      onTrigger: () => {
        FileBrowserActions.openItem(item.id);
      },
    });
  }

  return actions;
}

export function buildActions(
  results: OmniboxResponse,
  copiedAnswer: boolean,
  onCopyAnswer: () => void,
  helpDocsResults: Map<string, OmniboxHelpDocsResult | 'loading'>
): Array<OmniboxActionItem> {
  const actions: Array<OmniboxActionItem> = [];

  if (results.directAnswer) {
    const answerText = results.directAnswer;
    actions.push({
      id: 'direct-answer',
      title: answerText,
      description: copiedAnswer ? 'Copied to clipboard!' : undefined,
      group: 'Answer',
      resultType: 'direct_answer',
      onTrigger: () => {
        navigator.clipboard.writeText(answerText).catch((e) => {
          // eslint-disable-next-line no-console
          console.error('Failed to copy answer to clipboard', e);
        });
        onCopyAnswer();
      },
      closeOnTrigger: false,
    });
  }

  for (const result of results.searchResults) {
    actions.push({
      id: `search-${result.apiId}`,
      title: result.name,
      description: result.relevanceReason,
      group: 'Search Results',
      objectType: result.objectType,
      resultType: 'search_result',
      apiId: result.apiId,
      modifiedAt: result.modifiedAt ?? undefined,
      onTrigger: () => {
        FileBrowserActions.openItem(result.apiId);
      },
    });
  }

  for (const attachment of results.attachmentResults) {
    actions.push({
      id: `attachment-${attachment.attachmentApiId ?? attachment.entryApiId}-${attachment.attachmentName ?? 'unknown'}`,
      title: attachment.attachmentName ?? `Entry ${attachment.entryApiId}`,
      description: attachment.contentSnippet,
      group: 'Attachments',
      resultType: 'attachment',
      entryApiId: attachment.entryApiId,
      onTrigger: () => {
        openAttachmentPreview(attachment.entryApiId, attachment.noteLinkedFileId);
      },
    });
  }

  for (const suggestion of results.askSuggestions) {
    if (suggestion.type === 'help_docs') {
      const helpDocsResult = helpDocsResults.get(suggestion.rewrittenQuery);
      const isLoading = helpDocsResult === 'loading';
      const hasResult = helpDocsResult != null && helpDocsResult !== 'loading';
      const hasError = hasResult && helpDocsResult.error != null;
      const hasArticles = hasResult && !hasError && helpDocsResult.articles.length > 0;

      if (isLoading) {
        actions.push({
          id: `help-docs-loading-${suggestion.rewrittenQuery.slice(0, 30)}`,
          title: 'Searching help docs...',
          description: suggestion.reason,
          group: 'Help Docs',
          resultType: 'help_docs_loading',
          onTrigger: noop,
          closeOnTrigger: false,
        });
      } else if (hasArticles) {
        for (const article of helpDocsResult.articles) {
          actions.push({
            id: `help-doc-${article.url.slice(-30)}`,
            title: article.title,
            description: article.paraphrase,
            group: 'Help Docs',
            resultType: 'help_docs_article',
            onTrigger: () => {
              window.open(article.url, '_blank', 'noopener,noreferrer');
            },
          });
        }
      } else {
        // No results, error, or still pending — fall back to Ask Benchling
        actions.push({
          id: `ask-${suggestion.type}-${suggestion.rewrittenQuery.slice(0, 30)}`,
          title: hasError ? 'Help docs search failed' : `Ask: ${suggestion.rewrittenQuery}`,
          description: hasError ? helpDocsResult.error! : suggestion.reason,
          group: 'Ask Benchling',
          resultType: 'ask_suggestion',
          onTrigger: () => {
            openAskWithQuery(suggestion.rewrittenQuery);
          },
        });
      }
    } else {
      actions.push({
        id: `ask-${suggestion.type}-${suggestion.rewrittenQuery.slice(0, 30)}`,
        title: `Ask: ${suggestion.rewrittenQuery}`,
        description: suggestion.reason,
        group: 'Ask Benchling',
        resultType: 'ask_suggestion',
        onTrigger: () => {
          openAskWithQuery(suggestion.rewrittenQuery);
        },
      });
    }
  }

  return actions;
}
